import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { submitLead, formatApiError } from "@/lib/api";

const INITIAL = { name: "", whatsapp: "", email: "", houseType: "Type 36", budget: "", message: "" };

export default function LeadForm({ content }) {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await submitLead(form);
      setDone(true);
      toast.success(res.message || "Data berhasil dikirim");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-dark placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand";

  return (
    <section id="kontak" className="bg-fade-blue py-24" data-testid="lead-form-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              number="12"
              eyebrow="Hubungi Kami"
              title="Tertarik dengan Perumahan Kami?"
              description="Isi formulir di bawah ini dan tim marketing PT. CAHAYA SAKOFA MULIA akan menghubungi Anda untuk informasi unit, harga, dan jadwal kunjungan."
            />
            <Reveal delay={0.1} className="-mt-6 space-y-4">
              <div className="rounded-2xl border border-slate-100 bg-brand-mist p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Alamat</p>
                <p className="mt-1 text-sm font-medium text-brand-dark" data-testid="lead-address">{content.contact.address}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-brand-mist p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Email</p>
                <p className="mt-1 text-sm font-medium text-brand-dark" data-testid="lead-email">{content.contact.email}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_16px_50px_rgb(0,86,179,0.08)] sm:p-9">
              {done ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="lead-success-message">
                  <CheckCircle2 className="h-14 w-14 text-brand" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-brand-dark">Terima Kasih!</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Data Anda telah kami terima. Tim marketing kami akan segera menghubungi Anda melalui WhatsApp.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="lead-send-again"
                    className="mt-6 rounded-full border-2 border-brand px-6 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-white"
                  >
                    Kirim Pesan Lain
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="lead-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nama *</label>
                      <input id="lead-name" data-testid="lead-input-name" required value={form.name} onChange={set("name")} placeholder="Nama lengkap" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="lead-wa" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nomor WhatsApp *</label>
                      <input id="lead-wa" data-testid="lead-input-whatsapp" required value={form.whatsapp} onChange={set("whatsapp")} placeholder="08xxxxxxxxxx" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lead-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Email</label>
                    <input id="lead-email" data-testid="lead-input-email" type="email" value={form.email} onChange={set("email")} placeholder="email@contoh.com" className={inputCls} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="lead-type" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Pilihan Tipe Rumah</label>
                      <select id="lead-type" data-testid="lead-input-type" value={form.houseType} onChange={set("houseType")} className={inputCls}>
                        <option value="Type 36">Type 36</option>
                        <option value="Lainnya">Lainnya</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="lead-budget" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Budget</label>
                      <input id="lead-budget" data-testid="lead-input-budget" value={form.budget} onChange={set("budget")} placeholder="Contoh: Rp 300–400 juta" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lead-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Pesan</label>
                    <textarea id="lead-message" data-testid="lead-input-message" rows={4} value={form.message} onChange={set("message")} placeholder="Tuliskan pertanyaan atau kebutuhan Anda..." className={inputCls} />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="lead-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-deep active:scale-95 disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Saya Tertarik
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
